import React, {
  KeyboardEvent, RefObject, SyntheticEvent, useCallback, useEffect, useId, useRef, useState,
} from 'react';
import { Overlay, Popover } from 'react-bootstrap';
import {
  format, isBefore, isValid, parse, parseISO,
} from 'date-fns';

import DatePicker from 'components/DatePicker';
import Button from 'components/DesignSystem/Button';
import { DATE_REF_LABELS } from 'components/Flux/Personalized/MyStudies/FilterBar/PeriodFilter/ReferencePopover';
import usePopoverDismiss, {
  PERIOD_POPOVER_OFFSET,
  PopoverDismissReason,
} from 'components/Flux/Personalized/MyStudies/FilterBar/PeriodFilter/usePopoverDismiss';
import { defaultInterval, todayInSaoPaulo } from 'helper/flux/myStudiesPeriod';
import { DateOnly } from 'types/Flux/Personalized/myStudies';
import { MyStudiesDateInterval, MyStudiesDateRef } from 'types/Flux/Personalized/myStudiesFilters';

/** Formato que o campo mostra e aceita (D23, restrição da issue): o 'P' do ptBR. */
const DATE_PLACEHOLDER = 'dd/mm/aaaa';

/** Motivos de "Aplicar" desabilitado, ditos ao lado do botão e na descrição dele. */
export const MISSING_DATE_REASON = 'Preencha as duas datas no formato dd/mm/aaaa.';
export const INVERTED_DATES_REASON = 'A data inicial não pode ser depois da final.';

/**
 * Motivo para uma data dd/mm/aaaa que não existe no calendário.
 *
 * @param text - O texto digitado, já sem os espaços das pontas (ex.: "31/09/2026").
 * @returns Ex.: "31/09/2026 não existe no calendário."
 */
export function nonexistentDateReason(text: string): string {
  return `${text} não existe no calendário.`;
}

/** Formato do texto digitado, o mesmo que o campo mostra (o 'P' do ptBR). */
const TYPED_DATE_FORMAT = 'dd/MM/yyyy';

/** Texto com a forma dd/mm/aaaa, exista a data ou não ("31/09/2026"). */
const TYPED_DATE_SHAPE = /^\d{2}\/\d{2}\/\d{4}$/;

/**
 * Piso das datas: o do `isValid` do DatePicker (1º/1/1800), que recusa o que vier antes. O campo
 * recusa o mesmo, e o DatePicker o recebe em `minDate`.
 */
const MIN_DATE = new Date(1800, 0, 1);

/** O que entra nos campos focáveis do diálogo, para prender o foco. */
const FOCUSABLE_SELECTOR = 'input:not([disabled]), button:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

type RangeField = 'from' | 'to';

/** Uma data do rascunho. */
interface DraftField {
  /** Data entendida, ou `null` (vazia, fora de dd/mm/aaaa, antes de 1800 ou inexistente). */
  date: Date | null;
  /** Texto dd/mm/aaaa de uma data que não existe ("31/09/2026"), para o motivo; senão `null`. */
  nonexistent: string | null;
}

/** Rascunho do intervalo: só "Aplicar" o grava nos filtros (D23-B). */
type RangeDraft = Record<RangeField, DraftField>;

const draftField = (date: Date | null): DraftField => ({ date, nonexistent: null });

/**
 * Dia civil → `Date` à meia-noite local, que é como o DatePicker mostra e devolve um dia. Não há
 * fuso aqui: o dia de hoje em São Paulo já chega pronto do `todayInSaoPaulo`.
 */
function toPickerDate(day: DateOnly): Date {
  return parseISO(day);
}

/** `Date` do DatePicker → dia civil, pelas mesmas partes locais que o campo mostrou. */
function toDateOnly(date: Date): DateOnly {
  return format(date, 'yyyy-MM-dd') as DateOnly;
}

/**
 * Lê o texto digitado no campo, com a regra do `strictParsing` do DatePicker.
 *
 * @param text - Valor do input; os espaços das pontas não contam.
 * @returns A data, se o texto for exatamente uma data dd/mm/aaaa real a partir de 1800 ("1/9/2026",
 *   "01/09/26" e "10/09/0026" não são); sem data, o texto quando ele tem a forma dd/mm/aaaa mas a
 *   data não existe ("31/09/2026", "29/02/2027").
 */
function readTypedDate(text: string): DraftField {
  const value = text.trim();
  const date = parse(value, TYPED_DATE_FORMAT, new Date());
  if (isValid(date) && format(date, TYPED_DATE_FORMAT) === value) {
    return draftField(isBefore(date, MIN_DATE) ? null : date);
  }
  return { date: null, nonexistent: TYPED_DATE_SHAPE.test(value) ? value : null };
}

/**
 * Por que o rascunho não pode ser aplicado.
 *
 * @param draft - Rascunho do intervalo.
 * @returns O motivo, ou `null` quando "Aplicar" pode gravar. A data inexistente vem antes: é o
 *   motivo mais preciso.
 */
function invalidReason({ from, to }: RangeDraft): string | null {
  const nonexistent = from.nonexistent ?? to.nonexistent;
  if (nonexistent !== null) return nonexistentDateReason(nonexistent);
  if (from.date === null || to.date === null) return MISSING_DATE_REASON;
  if (toDateOnly(from.date) > toDateOnly(to.date)) return INVERTED_DATES_REASON;
  return null;
}

interface RangeFormProps {
  id: string;
  dateRef: MyStudiesDateRef;
  /** Intervalo aplicado, com que o rascunho começa; `null`: {1º dia do mês, hoje} em SP. */
  interval: MyStudiesDateInterval | null;
  onApply: (interval: MyStudiesDateInterval) => void;
}

/**
 * Conteúdo do popover: só existe com ele aberto, então cada abertura começa um rascunho novo.
 */
function RangeForm({
  id, dateRef, interval, onApply,
}: RangeFormProps) {
  const titleId = useId();
  const fromLabelId = useId();
  const toLabelId = useId();
  const fromInputId = useId();
  const toInputId = useId();
  const reasonId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState<RangeDraft>(() => {
    // O padrão (D23) é calculado uma vez, ao abrir
    const initial = interval ?? defaultInterval(todayInSaoPaulo(new Date()));
    return {
      from: draftField(toPickerDate(initial.from)),
      to: draftField(toPickerDate(initial.to)),
    };
  });
  const reason = invalidReason(draft);

  // Campo que recebe o foco de volta depois do Enter (ver `handleCalendarClose`). Enquanto isso,
  // focar o campo não reabre o calendário (`preventOpenOnFocus`).
  const [refocusField, setRefocusField] = useState<RangeField | null>(null);
  const refocusTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(refocusTimer.current), []);

  // Abrir leva o foco ao diálogo, não ao 1º campo: o foco no campo abriria o calendário por cima
  // das datas. Tab vai para a data inicial.
  useEffect(() => {
    dialogRef.current?.focus({ preventScroll: true });
  }, []);

  const setField = useCallback((field: RangeField, value: DraftField) => {
    setDraft((current) => ({ ...current, [field]: value }));
  }, []);

  // O Enter no campo com o calendário aberto fecha o calendário e tira o foco do campo (o
  // DatePicker faz `blur`): o foco ficaria no <body> com o popover aberto. Fechar o calendário com
  // o foco no <body> devolve o foco ao campo, depois do `blur` que o DatePicker adia ao fechar (um
  // `setTimeout`): daí um timer dentro do outro. No Esc e no clique num dia, o DatePicker já
  // devolve o foco ao campo nesse meio-tempo, e aqui nada muda; no Tab e no clique fora, o foco não
  // está no <body>.
  const handleCalendarClose = useCallback((field: RangeField) => {
    if (document.activeElement !== document.body) return;
    const inputId = field === 'from' ? fromInputId : toInputId;
    setRefocusField(field);
    clearTimeout(refocusTimer.current);
    refocusTimer.current = setTimeout(() => {
      refocusTimer.current = setTimeout(() => {
        if (document.activeElement === document.body) {
          document.getElementById(inputId)?.focus({ preventScroll: true });
        }
        setRefocusField(null);
      });
    });
  }, [fromInputId, toInputId]);

  // Cada tecla no campo: o texto vale como data só se for uma data dd/mm/aaaa completa. Sem isso,
  // um texto incompleto deixaria no rascunho a data anterior, que "Aplicar" gravaria (o DatePicker
  // só chama `onChange` quando entende uma data). Com `strictParsing`, ele entende só o mesmo
  // formato: sem ele, "20/09/20" viraria 2020 e "1/1/2", 2002. O clique no calendário não vem do
  // input e fica com o `onChange`.
  const handleRawChange = useCallback((field: RangeField, event?: SyntheticEvent<HTMLElement>) => {
    const input = event?.target;
    if (input instanceof HTMLInputElement) setField(field, readTypedDate(input.value));
  }, [setField]);

  const apply = useCallback(() => {
    const { from, to } = draft;
    if (from.date === null || to.date === null || reason !== null) return;
    onApply({ from: toDateOnly(from.date), to: toDateOnly(to.date) });
  }, [draft, reason, onApply]);

  // Foco preso no diálogo: Tab no último volta ao primeiro, Shift+Tab no primeiro vai ao último.
  // O calendário aberto fica no meio (logo depois do campo dele): nunca é o primeiro nem o último,
  // e o DatePicker cuida do Tab dentro dele.
  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    const dialog = dialogRef.current;
    if (event.key !== 'Tab' || dialog === null) return;
    const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === dialog)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  return (
    // O Tab é tratado no próprio diálogo, para prender o foco nos campos dele
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={dialogRef}
      id={id}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
      className="my-studies-range-dialog"
      onKeyDown={handleKeyDown}
    >
      <span id={titleId} className="my-studies-range-title">
        {`Intervalo de ${DATE_REF_LABELS[dateRef].toLowerCase()} (${DATE_PLACEHOLDER})`}
      </span>
      <div className="my-studies-range-fields">
        <span id={fromLabelId} className="visually-hidden">Data inicial</span>
        <DatePicker
          id={fromInputId}
          ariaLabelledBy={fromLabelId}
          selected={draft.from.date}
          minDate={MIN_DATE}
          onChange={(date) => setField('from', draftField(date))}
          onChangeRaw={(event) => handleRawChange('from', event)}
          onCalendarClose={() => handleCalendarClose('from')}
          preventOpenOnFocus={refocusField === 'from'}
          strictParsing
          placeholderText={DATE_PLACEHOLDER}
          className="my-studies-range-input"
          containerClass="my-studies-range-field"
        />
        <span className="my-studies-range-separator" aria-hidden="true">a</span>
        <span id={toLabelId} className="visually-hidden">Data final</span>
        <DatePicker
          id={toInputId}
          ariaLabelledBy={toLabelId}
          selected={draft.to.date}
          minDate={MIN_DATE}
          onChange={(date) => setField('to', draftField(date))}
          onChangeRaw={(event) => handleRawChange('to', event)}
          onCalendarClose={() => handleCalendarClose('to')}
          preventOpenOnFocus={refocusField === 'to'}
          strictParsing
          placeholderText={DATE_PLACEHOLDER}
          className="my-studies-range-input"
          containerClass="my-studies-range-field"
        />
      </div>
      {reason && <p id={reasonId} className="my-studies-range-reason">{reason}</p>}
      <Button
        small
        className="my-studies-range-apply"
        aria-disabled={reason !== null || undefined}
        aria-describedby={reason ? reasonId : undefined}
        onClick={apply}
      >
        Aplicar
      </Button>
    </div>
  );
}

export interface RangePopoverProps {
  /** Popover aberto. */
  show: boolean;
  /** Onde o popover abre: embaixo e à direita dos segmentos do período (.dc.html:189-192). */
  target: RefObject<HTMLElement | null>;
  /**
   * Onde o popover é montado no DOM (padrão: o `<body>`). O `PeriodFilter` passa um elemento logo
   * depois dos segmentos, para o leitor de tela encontrar o diálogo junto deles.
   */
  container?: RefObject<HTMLElement | null>;
  /** `id` do diálogo, par do `aria-controls` do segmento "Intervalo…". */
  id: string;
  /** Referência do período, para o texto "Intervalo de última execução (dd/mm/aaaa)". */
  dateRef: MyStudiesDateRef;
  /**
   * Intervalo aplicado, com que o rascunho começa. `null` fora do período "Intervalo…": o
   * rascunho começa em {1º dia do mês, hoje} em São Paulo, calculado ao abrir (D23).
   */
  interval: MyStudiesDateInterval | null;
  /** Recebe o intervalo no clique em "Aplicar", válido e em ordem. */
  onApply: (interval: MyStudiesDateInterval) => void;
  /** Pede para fechar sem aplicar (Esc ou clique fora). */
  onClose: (reason: PopoverDismissReason) => void;
}

/**
 * Popover de "Intervalo…" do período (#1996, D23 opção B): duas datas do DatePicker do DS (ptBR,
 * formato 'P' = dd/mm/aaaa) separadas por "a" e o botão "Aplicar" (.dc.html:191-201).
 *
 * As datas são um rascunho local: só "Aplicar" chama `onApply`; Esc e clique fora fecham sem
 * aplicar, e reabrir começa de novo do intervalo aplicado (ou do padrão). "Aplicar" fica
 * `aria-disabled` sem uma das datas (ou com uma incompleta, ou antes de 1800), com uma data que
 * não existe ("31/09/2026") e com a inicial depois da final, com o motivo ao lado e na descrição do
 * botão.
 *
 * Diálogo modal (`aria-modal`) com o foco preso: abre com o foco nele, Tab circula entre as datas e
 * "Aplicar". Com o calendário de uma data aberto, o 1º Esc fecha só o calendário, e o Enter fecha
 * o calendário deixando o foco no campo.
 */
export default function RangePopover({
  show,
  target,
  container,
  id,
  dateRef,
  interval,
  onApply,
  onClose,
}: RangePopoverProps) {
  const handleHide = usePopoverDismiss(show, onClose);

  return (
    <Overlay
      show={show}
      target={target}
      container={container}
      placement="bottom-end"
      offset={PERIOD_POPOVER_OFFSET}
      flip
      rootClose
      onHide={handleHide}
      transition={false}
    >
      {/* O papel de diálogo fica no conteúdo, que tem o foco; a casca do Popover não é tooltip */}
      <Popover role={undefined} className="my-studies-period-popover my-studies-range-popover">
        <RangeForm id={id} dateRef={dateRef} interval={interval} onApply={onApply} />
      </Popover>
    </Overlay>
  );
}
